import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useTabStore } from "@/stores/tabStore";
import { X, XCircle, ArrowLeftToLine, ArrowRightToLine } from "lucide-react";
import ContextMenu, { ContextMenuItem } from "@/components/ui/ContextMenu";

interface ContextMenuState {
  isOpen: boolean;
  x: number;
  y: number;
  tabId: string;
  tabIndex: number;
}

export function TabHistory() {
  const navigate = useNavigate();
  const { tabs, removeTab, setActiveTab, moveTab, closeOtherTabs, closeTabsToRight, closeTabsToLeft } = useTabStore();

  // 드래그 상태
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

  // 컨텍스트 메뉴 상태
  const [contextMenu, setContextMenu] = useState<ContextMenuState>({
    isOpen: false,
    x: 0,
    y: 0,
    tabId: "",
    tabIndex: -1,
  });

  const handleTabClick = (tabId: string, path: string) => {
    if (draggedIndex !== null) return;
    setActiveTab(tabId);
    navigate(path);
  };

  // 우클릭 핸들러
  const handleContextMenu = (e: React.MouseEvent, tabId: string, index: number) => {
    e.preventDefault();
    e.stopPropagation();
    setContextMenu({
      isOpen: true,
      x: e.clientX,
      y: e.clientY,
      tabId,
      tabIndex: index,
    });
  };

  const closeContextMenu = () => {
    setContextMenu(prev => ({ ...prev, isOpen: false }));
  };

  // 컨텍스트 메뉴 액션
  const handleCloseTab = () => {
    if (tabs.length > 1) {
      const currentTab = tabs.find(tab => tab.id === contextMenu.tabId);
      if (currentTab?.isActive) {
        const tabIndex = tabs.findIndex(tab => tab.id === contextMenu.tabId);
        const remainingTabs = tabs.filter(tab => tab.id !== contextMenu.tabId);
        const nextActiveTab =
          tabIndex < remainingTabs.length ? remainingTabs[tabIndex] : remainingTabs[remainingTabs.length - 1];
        if (nextActiveTab) navigate(nextActiveTab.path);
      }
      removeTab(contextMenu.tabId);
    }
  };

  const handleCloseOtherTabs = () => {
    const targetTab = tabs.find(t => t.id === contextMenu.tabId);
    if (targetTab) {
      navigate(targetTab.path);
      closeOtherTabs(contextMenu.tabId);
    }
  };

  const handleCloseTabsToRight = () => {
    const activeIndex = tabs.findIndex(t => t.isActive);
    if (activeIndex > contextMenu.tabIndex) {
      const targetTab = tabs[contextMenu.tabIndex];
      navigate(targetTab.path);
    }
    closeTabsToRight(contextMenu.tabId);
  };

  const handleCloseTabsToLeft = () => {
    const activeIndex = tabs.findIndex(t => t.isActive);
    if (activeIndex < contextMenu.tabIndex) {
      const targetTab = tabs[contextMenu.tabIndex];
      navigate(targetTab.path);
    }
    closeTabsToLeft(contextMenu.tabId);
  };

  // 컨텍스트 메뉴 아이템
  const contextMenuItems: ContextMenuItem[] = useMemo(
    () => [
      {
        label: "탭 닫기 ",
        icon: <X size={14} />,
        onClick: handleCloseTab,
        disabled: tabs.length <= 1,
      },
      {
        label: "다른 탭 모두 닫기 ",
        icon: <XCircle size={14} />,
        onClick: handleCloseOtherTabs,
        disabled: tabs.length <= 1,
      },
      { divider: true },
      {
        label: "왼쪽 탭 모두 닫기 ",
        icon: <ArrowLeftToLine size={14} />,
        onClick: handleCloseTabsToLeft,
        disabled: contextMenu.tabIndex === 0,
      },
      {
        label: "오른쪽 탭 모두 닫기 ",
        icon: <ArrowRightToLine size={14} />,
        onClick: handleCloseTabsToRight,
        disabled: contextMenu.tabIndex === tabs.length - 1,
      },
    ],
    [tabs.length, contextMenu.tabIndex, contextMenu.tabId]
  );

  // 드래그 핸들러
  const handleDragStart = (e: React.DragEvent, index: number) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", index.toString());
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (dragOverIndex !== index) {
      setDragOverIndex(index);
    }
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
  };

  const handleDrop = (e: React.DragEvent, toIndex: number) => {
    e.preventDefault();
    if (draggedIndex !== null && draggedIndex !== toIndex) {
      moveTab(draggedIndex, toIndex);
    }
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleTabClose = (e: React.MouseEvent, tabId: string) => {
    e.stopPropagation();
    if (tabs.length > 1) {
      const currentTab = tabs.find(tab => tab.id === tabId);
      const isCurrentTabActive = currentTab?.isActive;

      if (isCurrentTabActive) {
        const tabIndex = tabs.findIndex(tab => tab.id === tabId);
        const remainingTabs = tabs.filter(tab => tab.id !== tabId);

        let nextActiveTab;
        if (tabIndex < remainingTabs.length) {
          nextActiveTab = remainingTabs[tabIndex];
        } else {
          nextActiveTab = remainingTabs[remainingTabs.length - 1];
        }

        if (nextActiveTab) {
          navigate(nextActiveTab.path);
        }
      }

      removeTab(tabId);
    }
  };

  // 탭 모양 결정
  const getTabClipPath = (index: number, totalTabs: number) => {
    if (totalTabs === 1) {
      return "polygon(0 0, calc(100% - 12px) 0, 100% 100%, 0 100%)";
    } else if (index === 0) {
      return "polygon(0 0, calc(100% - 12px) 0, 100% 100%, 0 100%)";
    } else if (index === totalTabs - 1) {
      return "polygon(12px 0, calc(100% - 12px) 0, 100% 100%, 0 100%)";
    } else {
      return "polygon(12px 0, calc(100% - 12px) 0, 100% 100%, 0 100%)";
    }
  };

  if (tabs.length === 0) {
    return null;
  }

  return (
    <div className="pt-3 bg-[#F6F6F6]">
      <div className="max-w-full mx-auto px-4">
        <div className="flex items-start overflow-x-auto">
          {tabs.map((tab, index) => (
            <div
              key={tab.id}
              className={`relative cursor-pointer transition-colors min-w-0 flex-shrink-0 ${
                draggedIndex === index ? "opacity-50" : ""
              } ${dragOverIndex === index && draggedIndex !== index ? "scale-105" : ""}`}
              onClick={() => handleTabClick(tab.id, tab.path)}
              onContextMenu={e => handleContextMenu(e, tab.id, index)}
              style={{ zIndex: tab.isActive ? 20 : 10 - index }}
              draggable
              onDragStart={e => handleDragStart(e, index)}
              onDragOver={e => handleDragOver(e, index)}
              onDragLeave={handleDragLeave}
              onDrop={e => handleDrop(e, index)}
              onDragEnd={handleDragEnd}
            >
              <div
                className={`relative px-4 py-2 transition-all duration-200 ${
                  tab.isActive
                    ? "bg-[#FFF] text-gray-800 shadow-figma-tab"
                    : "bg-[#D9D9D9] text-[#333333] hover:bg-gray-200"
                } ${index === 0 ? "rounded-tl-md" : ""} ${index === tabs.length - 1 ? "rounded-tr-md" : ""}`}
                style={{
                  clipPath: getTabClipPath(index, tabs.length),
                  minWidth: "160px",
                  maxWidth: "200px",
                  height: "30px",
                }}
              >
                <div className="flex items-center justify-between h-full">
                  <span className="text-sm font-medium truncate mr-2 select-none">{tab.title}</span>
                  {tabs.length > 1 && (
                    <button
                      onClick={e => handleTabClose(e, tab.id)}
                      className={`p-0.5 rounded-full transition-colors flex-shrink-0 cursor-pointer ${
                        tab.isActive ? "hover:bg-gray-200" : "hover:bg-gray-400"
                      }`}
                    >
                      <X size={16} />
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* 컨텍스트 메뉴 */}
      <ContextMenu
        isOpen={contextMenu.isOpen}
        x={contextMenu.x}
        y={contextMenu.y}
        items={contextMenuItems}
        onClose={closeContextMenu}
      />
    </div>
  );
}
